import {
  getTransportPolicy,
  type TransportRequestConfig,
} from "@/infrastructure/http/transport-policy";
import { getOrCreateRequestId } from "@/infrastructure/http/request-id";
import {
  buildHttpTransportError,
  isAbortError,
  isRetryableNetworkError,
  TransportError,
} from "@/infrastructure/http/transport-error";
import {
  normalizeRecordDetailError,
  RECORD_DETAIL_TRANSPORT_CONFIG,
  type RecordDetailRequestContext,
} from "@/lib/record-detail-transport";
import type { DetailedReportResponse } from "@/lib/types";

const SHARED_API_BASE_URL =
  process.env.NEXT_PUBLIC_CUSTDEV_API_URL || "http://localhost:8200";

async function requestPublicJson<T>(
  url: string,
  context: string,
  transportConfig: TransportRequestConfig = RECORD_DETAIL_TRANSPORT_CONFIG
): Promise<T> {
  const policy = getTransportPolicy("client", transportConfig);
  const requestId = getOrCreateRequestId(undefined, "shared-insight");
  const maxAttempts = Math.max(1, policy.maxAttempts);

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    const abortController = new AbortController();
    let timeoutTriggered = false;
    const timeoutId = setTimeout(() => {
      timeoutTriggered = true;
      abortController.abort();
    }, policy.timeoutMs);

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "X-Request-ID": requestId,
          "X-Retry-Attempt": String(attempt),
        },
        signal: abortController.signal,
      });

      if (response.ok) {
        return (await response.json()) as T;
      }

      throw buildHttpTransportError(
        `${context}: ${response.status} ${response.statusText}`,
        response.status,
        requestId
      );
    } catch (error) {
      if (timeoutTriggered || isAbortError(error)) {
        throw new TransportError(
          `Request timeout after ${policy.timeoutMs}ms`,
          {
            code: "TIMEOUT",
            retryable: false,
            requestId,
            cause: error,
          }
        );
      }

      if (isRetryableNetworkError(error)) {
        throw new TransportError("Network request failed", {
          code: "NETWORK_ERROR",
          retryable: false,
          requestId,
          cause: error,
        });
      }

      throw error instanceof Error ? error : new Error(String(error));
    } finally {
      clearTimeout(timeoutId);
    }
  }

  throw new Error(`${context}: request failed`);
}

export async function getSharedReport(
  recordId: string,
  version?: number | null,
  requestContext?: RecordDetailRequestContext
): Promise<DetailedReportResponse> {
  if (recordId === "00000000-0000-0000-0000-000000000000") {
    const baseUrl = SHARED_API_BASE_URL.replace(/\/$/, "");
    const demoUrl = `${baseUrl}/insight/intro`;
    try {
      return await requestPublicJson<DetailedReportResponse>(
        demoUrl,
        "Failed to fetch demo report",
        RECORD_DETAIL_TRANSPORT_CONFIG
      );
    } catch (error) {
      console.warn("Demo endpoint failed, falling back to public endpoint:", error);
    }
  }

  const baseUrl = SHARED_API_BASE_URL.replace(/\/$/, "");
  let url = `${baseUrl}/insight/public/${recordId}/`;

  if (version !== null && version !== undefined) {
    url = `${url}?version=${version}`;
  }

  try {
    return await requestPublicJson<DetailedReportResponse>(
      url,
      "Failed to fetch shared report",
      RECORD_DETAIL_TRANSPORT_CONFIG
    );
  } catch (error) {
    throw normalizeRecordDetailError(error, {
      scope: "public",
      recordId,
      version: version ?? null,
      ...requestContext,
    });
  }
}
